/**
 * <cd-product-reviews-fetcher>
 *
 * Client-side "Load more reviews" pagination + sort for the PDP reviews
 * section. The first page (~5–8 reviews) is rendered server-side from the
 * `product.metafields.judgeme.widget` HTML blob. This component fetches
 * additional pages from Judge.me's public widget API and appends them to
 * the existing list, matching the SSR card markup exactly.
 *
 * API endpoint (verified live against paintingtogogh.myshopify.com):
 *   GET https://judge.me/api/v1/widgets/product_review
 *     ?api_token=<public_token>
 *     &shop_domain=<shop>.myshopify.com
 *     &platform=shopify
 *     &external_id=<shopify_product_id>
 *     &page=<n>            (1-indexed; first page = SSR'd reviews)
 *     &per_page=<n>        (size of each page)
 *     &sort_by=<token>     (optional; see SORT TOKENS below)
 *     &sort_dir=<asc|desc> (optional; pairs with sort_by=rating)
 *
 * Response shape: JSON
 *   {
 *     "product_external_id": <number>,
 *     "widget": "<div class='jdgm-rev-widg' ...> ... </div>"  // HTML string
 *   }
 *
 * The `widget` HTML is the same format the Liquid section already parses,
 * so we reuse the same field selectors here. When the API returns fewer
 * reviews than `per_page`, we know we hit the last page and hide the
 * button.
 *
 * SORT TOKENS — extracted from Judge.me's own widget code
 * (cdnwidget.judge.me/widget/common.js) by reading the dropdown
 * change handler's lookup table that maps UI tokens → API tokens.
 * The dropdown's hyphenated values (`pictures-first`, `most-recent`)
 * are NOT what the API accepts; the API uses underscored tokens.
 *
 * Earlier this file shipped `sort_by=pictures` and `sort_by=date` —
 * both silently fall through to the API's curated default order
 * (chronological-ish), which is why "Pictures first" and "Most
 * recent" returned identical reviews. Same bug existed on the
 * shop-wide all-reviews endpoint and was fixed there too.
 *
 *   UI value         → API params
 *   ───────────────────────────────────────────────────────────────
 *   newest           → sort_by=created_at&sort_dir=desc
 *   highest_rating   → sort_by=rating&sort_dir=desc
 *   lowest_rating    → sort_by=rating&sort_dir=asc
 *   pictures         → sort_by=pictures_first
 *   (with-pictures)  → sort_by=with_pictures
 *   (videos-first)   → sort_by=videos_first
 *   (most-helpful)   → sort_by=most_helpful
 *
 * Sort change resets the rendered list (clearing SSR'd cards), refetches
 * page 1 with the chosen `sort_by`, and resumes pagination from there.
 * The "Most recent" branch refetches too — simpler than caching SSR DOM.
 */

(function () {
  if (customElements.get('cd-product-reviews-fetcher')) return;
  if (!window.CdJudgemeFetcher) {
    console.error('cd-product-reviews-fetcher: requires cd-judgeme-fetcher.js loaded first');
    return;
  }

  // Shared widget-parsing helpers — see assets/cd-judgeme-fetcher.js.
  var pickAttr = window.CdJudgemeFetcher.pickAttr;
  var pickText = window.CdJudgemeFetcher.pickText;
  var formatDate = window.CdJudgemeFetcher.formatDate;
  var escapeHtml = window.CdJudgemeFetcher.escapeHtml;
  var upgradePicUrl = window.CdJudgemeFetcher.upgradePicUrl;

  // Dropdown value → API params. Tokens match Judge.me's internal
  // mapping table (see header comment). Do NOT use the dropdown's
  // hyphenated values here — the API silently falls through to
  // default order if it doesn't recognize the token.
  var SORT_PARAMS = {
    newest:         { sort_by: 'created_at',    sort_dir: 'desc' },
    highest_rating: { sort_by: 'rating',        sort_dir: 'desc' },
    lowest_rating:  { sort_by: 'rating',        sort_dir: 'asc' },
    pictures:       { sort_by: 'pictures_first' }
  };

  function parseReviews(widgetHtml) {
    if (!widgetHtml) return [];
    // Each review begins with this delimiter in Judge.me's widget HTML.
    var chunks = widgetHtml.split("<div class='jdgm-rev jdgm-divider-top'");
    var out = [];
    for (var i = 1; i < chunks.length; i++) {
      var c = chunks[i];
      var id = pickAttr(c, 'data-review-id');
      if (!id) continue;
      var verified = pickAttr(c, 'data-verified-buyer') === 'true';
      var score = parseInt(pickAttr(c, 'data-score'), 10);
      if (isNaN(score) || score < 1) score = 5;
      if (score > 5) score = 5;

      // Timestamp: jdgm-rev__timestamp ... data-content='2024-01-15 12:00:00 UTC'
      var tsMatch = c.match(/jdgm-rev__timestamp[^>]*data-content='([^']*)'/);
      var date = tsMatch ? formatDate(tsMatch[1]) : '';

      var author = pickText(c, "jdgm-rev__author'>", '</span>') || 'Anonymous';
      var title = pickText(c, "jdgm-rev__title'>", '</b>');
      var body = pickText(c, "jdgm-rev__body'>", '</div>');

      // Optional location: " (City, ST) "
      var loc = '';
      var locMatch = c.match(/jdgm-rev__location'[^>]*>([\s\S]*?)<\/span>/);
      if (locMatch) {
        loc = locMatch[1].replace(/<[^>]+>/g, '').replace(/[()]/g, '').trim();
      }

      // Customer photos — extract <img src="..."> URLs from the
      // jdgm-rev__pics block; rendered as small lazy thumbnails.
      var pics = [];
      var picsMatch = c.match(/jdgm-rev__pics[^>]*>([\s\S]*?)<\/div>/);
      if (picsMatch) {
        var imgRe = /<img[^>]+src=['"]([^'"]+)['"]/g;
        var m;
        while ((m = imgRe.exec(picsMatch[1])) !== null) pics.push(m[1]);
      }

      out.push({ id: id, score: score, verified: verified, date: date, author: author, title: title, body: body, location: loc, pics: pics });
    }
    return out;
  }

  function buildCard(review, index, totalRendered) {
    // Flat 0° tilt — every card looks the same.
    var rot = 0;
    var stars = '';
    for (var s = 1; s <= 5; s++) {
      stars += '<span class="cd-product-reviews__star' + (s <= review.score ? ' is-filled' : '') + '" aria-hidden="true">★</span>';
    }

    // Verified pill — sits in the meta line (next to date/location)
    // so the row stays horizontally aligned regardless of whether the
    // hand-lettered author wraps. Mirrors the SSR Liquid markup.
    var verifiedHtml = review.verified
      ? '<span class="cd-product-reviews__verified" title="Verified purchase" aria-label="Verified purchase">'
        + '<svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" width="12" height="12" aria-hidden="true">'
        + '<path d="M3 8.5L6.5 12L13 4.5" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>'
        + '<span class="cd-product-reviews__verified-text">Verified</span></span>'
      : '';

    var titleHtml = review.title ? '<h3 class="cd-product-reviews__title">' + escapeHtml(review.title) + '</h3>' : '';
    // Long bodies (>280 chars) get the clamp class + Read-more button.
    // Mirrors the Liquid SSR threshold so dynamically loaded cards
    // behave the same as the first paint.
    var bodyHtml = '';
    if (review.body) {
      var isLong = review.body.length > 280;
      bodyHtml = '<p class="cd-product-reviews__body">' + escapeHtml(review.body) + '</p>';
      if (isLong) {
        bodyHtml += '<button type="button" class="cd-product-reviews__expand" data-cd-expand>Read more</button>';
      }
    }
    var picsHtml = '';
    if (review.pics && review.pics.length) {
      var picMax = Math.min(review.pics.length, 3);
      picsHtml = '<div class="cd-product-reviews__pics">';
      for (var p = 0; p < picMax; p++) {
        // Thumbnails display ~100-200px in a 3-up grid, so request 400/800 for
        // 1x/2x; the 1600 full-size loads only on click (the lightbox href).
        var raw = review.pics[p];
        var thumb = upgradePicUrl(raw, 400);
        var thumb2x = upgradePicUrl(raw, 800);
        var full = upgradePicUrl(raw, 1600);
        var thumbE = escapeHtml(thumb);
        var thumb2xE = escapeHtml(thumb2x);
        var fullE = escapeHtml(full);
        picsHtml += '<a class="cd-product-reviews__pic" href="' + fullE + '" aria-label="Open customer photo">'
          + '<img src="' + thumbE + '" srcset="' + thumbE + ' 1x, ' + thumb2xE + ' 2x" loading="lazy" decoding="async" alt="" width="400" height="400"></a>';
      }
      picsHtml += '</div>';
    }
    var locHtml = review.location
      ? '<span class="cd-product-reviews__loc-sep" aria-hidden="true">·</span><span class="cd-product-reviews__location">' + escapeHtml(review.location) + '</span>'
      : '';
    var dateHtml = review.date ? '<span class="cd-product-reviews__date">' + escapeHtml(review.date) + '</span>' : '';
    // Verified pill needs its own separator if there's any preceding
    // meta content (date or location) — keeps the inline flow clean.
    var verifiedSep = (review.verified && (review.date || review.location))
      ? '<span class="cd-product-reviews__loc-sep" aria-hidden="true">·</span>'
      : '';

    var li = document.createElement('li');
    li.className = 'cd-product-reviews__card-wrap is-revealed';
    li.setAttribute('data-review-id', review.id);
    li.setAttribute('data-score', String(review.score));
    li.innerHTML =
      '<article class="cd-product-reviews__card" style="--cd-card-rot: ' + rot + 'deg;">'
      + '<div class="cd-product-reviews__stars" role="img" aria-label="' + review.score + ' out of 5 stars">' + stars + '</div>'
      + titleHtml + bodyHtml + picsHtml
      + '<div class="cd-product-reviews__byline">'
      + '<div class="cd-product-reviews__byline-text">'
      + '<div class="cd-product-reviews__author">' + escapeHtml(review.author) + '</div>'
      + '<div class="cd-product-reviews__meta">' + dateHtml + locHtml + verifiedSep + verifiedHtml + '</div>'
      + '</div></div></article>';
    return li;
  }

  function buildSkeleton() {
    var li = document.createElement('li');
    li.className = 'cd-product-reviews__card-wrap cd-product-reviews__skeleton';
    li.setAttribute('aria-hidden', 'true');
    li.innerHTML = '<article class="cd-product-reviews__card" style="--cd-card-rot: 0deg;">'
      + '<div class="cd-product-reviews__skeleton-stars"></div>'
      + '<div class="cd-product-reviews__skeleton-line cd-product-reviews__skeleton-line--lg"></div>'
      + '<div class="cd-product-reviews__skeleton-line"></div>'
      + '<div class="cd-product-reviews__skeleton-line cd-product-reviews__skeleton-line--short"></div>'
      + '<div class="cd-product-reviews__skeleton-byline"></div></article>';
    return li;
  }

  class CDProductReviewsFetcher extends HTMLElement {
    connectedCallback() {
      this.token = this.getAttribute('data-public-token') || '';
      this.shopDomain = this.getAttribute('data-shop-domain') || '';
      this.productId = this.getAttribute('data-product-id') || '';
      this.perLoad = parseInt(this.getAttribute('data-per-load'), 10) || 8;
      this.maxTotal = parseInt(this.getAttribute('data-max-total'), 10) || 50;
      // Initial target — number of cards we want visible on first paint.
      // SSR can only render whatever Judge.me's metafield ships (~5),
      // so if the target is higher we auto-fetch the rest on connect to
      // keep rows even (4 per row × 2 = 8).
      this.initialTarget = parseInt(this.getAttribute('data-initial-target'), 10) || 8;
      this.list = this.querySelector('.cd-product-reviews__list');
      this.button = this.querySelector('[data-cd-reviews-load-more]');
      this.errorEl = this.querySelector('[data-cd-reviews-error]');
      // Sort + histogram live in the section header (siblings of this
      // element), so query from the section root, not from `this`.
      this.section = this.closest('.cd-product-reviews');
      this.sortSelect = this.section ? this.section.querySelector('[data-cd-reviews-sort]') : null;

      // Cache the original "Load more" button label up front so the
      // sort-fetch path can restore it after temporarily replacing the
      // text with "Loading...". (Pulled from this.origLabel here too so
      // the loadMore path's previously-existing self-init still works.)
      this.loadMoreLabel = (this.button && this.button.textContent.trim()) || 'Load more reviews';
      // Cache the canonical error message text so we can restore it after
      // the sort-fetch path overwrites it with a sort-specific message.
      this.defaultErrorText = (this.errorEl && this.errorEl.textContent.trim())
        || "Couldn't load more reviews. Try again in a moment.";

      if (!this.list || !this.token || !this.shopDomain || !this.productId) {
        if (this.button) this.button.hidden = true;
        if (this.sortSelect) this.sortSelect.disabled = true;
        return;
      }

      // Track already-rendered review IDs (from SSR + future fetches).
      this.seen = new Set();
      var rendered = this.list.querySelectorAll('[data-review-id]');
      for (var i = 0; i < rendered.length; i++) {
        var rid = rendered[i].getAttribute('data-review-id');
        if (rid) this.seen.add(rid);
      }
      this.renderedCount = rendered.length;

      // Page 1 is what the SSR metafield contains (~5–8 reviews). Start
      // pagination at page 2 for the default sort. After a sort change
      // we refetch page 1 and reset nextPage to 2.
      this.nextPage = 2;
      this.loading = false;
      this.allLoaded = false;
      // Default to pictures-first — visually richer + matches the
      // dropdown's default-selected option. The SSR'd cards are
      // reordered client-side below so the visible list aligns with
      // this immediately (no flicker, no extra API call). Force-sync
      // the dropdown's value to currentSort so a bfcache restore
      // (back/forward nav) can't leave them out of step.
      this.currentSort = 'pictures';
      if (this.sortSelect) this.sortSelect.value = this.currentSort;
      // Upgrade SSR'd photo URLs (Liquid emits raw thumbs at w=160).
      // The regex used here also runs end-of-string `w=N`, which a
      // Liquid `replace` chain can't.
      this.upgradeSsrPhotoUrls();
      this.reorderPicturesFirst();

      if (this.button) {
        this.button.addEventListener('click', this.onLoadMore.bind(this));
      }
      if (this.sortSelect) {
        this.sortSelect.addEventListener('change', this.onSortChange.bind(this));
      }

      // Histogram rows — click to filter to that rating. The filter
      // round-trips to Judge.me via `filter_rating=N` (their widget
      // API param, verified live) so we see ALL reviews of that
      // rating, not just whichever subset happens to be in the DOM.
      // Re-clicking the same row clears the filter. Sort change also
      // clears it. Earlier this filter was pure client-side hiding,
      // which broke when the SSR'd cards (mostly 5-star on a 5-star-
      // heavy product) didn't include any 4★/3★ reviews — clicking
      // those rows hid every card, showing an empty list.
      this.activeRating = 0;
      this.histogramRows = this.section ? this.section.querySelectorAll('[data-cd-hist-rating]') : [];
      var self = this;
      for (var h = 0; h < this.histogramRows.length; h++) {
        this.histogramRows[h].addEventListener('click', function (e) {
          if (self.loading) return;
          var rating = parseInt(this.getAttribute('data-cd-hist-rating'), 10);
          if (!rating) return;
          var next = (self.activeRating === rating) ? 0 : rating;
          self.activeRating = next;
          self.applyRatingFilter(next);
        });
      }

      // Auto-fill: if SSR rendered fewer cards than the initial target
      // (because Judge.me's metafield only ships ~5), fetch the gap.
      // Deferred via IntersectionObserver so users who never scroll to
      // reviews don't pay the API round-trip. 600px rootMargin means
      // the fetch fires before the user reaches the section, so the
      // top-up is already done by the time they look.
      if (this.renderedCount < this.initialTarget && this.renderedCount > 0) {
        if ('IntersectionObserver' in window) {
          var self = this;
          var observer = new IntersectionObserver(function (entries) {
            for (var i = 0; i < entries.length; i++) {
              if (entries[i].isIntersecting) {
                observer.disconnect();
                self.autoFillInitial();
                break;
              }
            }
          }, { rootMargin: '600px 0px' });
          observer.observe(this);
        } else {
          this.autoFillInitial();
        }
      }
    }

    /**
     * Rewrite every SSR'd `<img>` inside `.cd-product-reviews__pic`
     * so the `src`/`href` use a high-res Imgix width (800 / 1600).
     * Liquid couldn't do this safely because end-of-string `w=N`
     * defeats literal `replace` chains.
     */
    upgradeSsrPhotoUrls() {
      if (!this.list) return;
      var anchors = this.list.querySelectorAll('.cd-product-reviews__pic');
      for (var i = 0; i < anchors.length; i++) {
        var a = anchors[i];
        var img = a.querySelector('img');
        if (!img) continue;
        var raw = img.getAttribute('src') || '';
        if (!raw) continue;
        var thumb = upgradePicUrl(raw, 400);
        var thumb2x = upgradePicUrl(raw, 800);
        var full = upgradePicUrl(raw, 1600);
        if (thumb && thumb !== raw) img.setAttribute('src', thumb);
        if (thumb && thumb2x) img.setAttribute('srcset', thumb + ' 1x, ' + thumb2x + ' 2x');
        if (full) a.setAttribute('href', full);
      }
    }

    /**
     * Reorder the SSR'd cards so any with photos sit before any
     * without. Stable within each group (preserves Judge.me's curated
     * order otherwise). Pure DOM, no API call, no flicker.
     */
    reorderPicturesFirst() {
      if (!this.list) return;
      var cards = this.list.querySelectorAll('.cd-product-reviews__card-wrap');
      if (cards.length < 2) return;
      var withPics = [];
      var withoutPics = [];
      for (var i = 0; i < cards.length; i++) {
        if (cards[i].querySelector('.cd-product-reviews__pics')) {
          withPics.push(cards[i]);
        } else {
          withoutPics.push(cards[i]);
        }
      }
      if (withPics.length === 0 || withoutPics.length === 0) return;
      var frag = document.createDocumentFragment();
      for (var p = 0; p < withPics.length; p++) frag.appendChild(withPics[p]);
      for (var n = 0; n < withoutPics.length; n++) frag.appendChild(withoutPics[n]);
      this.list.appendChild(frag);
    }

    /**
     * Top-up the SSR'd list to `initialTarget` cards by paging the
     * API once (silently — no skeletons, no button toggle). De-dupes
     * against already-rendered IDs. Uses the current sort so the
     * gap-fillers match the displayed order (e.g. pictures-first).
     */
    async autoFillInitial() {
      var gap = this.initialTarget - this.renderedCount;
      if (gap <= 0) return;
      this.loading = true;
      var controller = (typeof AbortController !== 'undefined') ? new AbortController() : null;
      var timeoutId = setTimeout(function () { if (controller) controller.abort(); }, 10000);
      try {
        // Pull a generous page so we can dedupe against the SSR'd 5
        // and still have `gap` fresh ones to append.
        var url = 'https://judge.me/api/v1/widgets/product_review'
          + '?api_token=' + encodeURIComponent(this.token)
          + '&shop_domain=' + encodeURIComponent(this.shopDomain)
          + '&platform=shopify'
          + '&external_id=' + encodeURIComponent(this.productId)
          + '&page=1'
          + '&per_page=' + Math.max(this.initialTarget + 5, 10);
        var sortParams = SORT_PARAMS[this.currentSort];
        if (sortParams) {
          if (sortParams.sort_by) url += '&sort_by=' + encodeURIComponent(sortParams.sort_by);
          if (sortParams.sort_dir) url += '&sort_dir=' + encodeURIComponent(sortParams.sort_dir);
        }
        var fetchOpts = { method: 'GET', headers: { Accept: 'application/json' } };
        if (controller) fetchOpts.signal = controller.signal;
        var res = await fetch(url, fetchOpts);
        clearTimeout(timeoutId);
        if (!res.ok) return;
        var json = await res.json();
        var reviews = parseReviews(json && json.widget);
        var added = 0;
        for (var i = 0; i < reviews.length && added < gap; i++) {
          if (this.seen.has(reviews[i].id)) continue;
          var node = buildCard(reviews[i], added, this.renderedCount);
          this.list.appendChild(node);
          this.seen.add(reviews[i].id);
          this.renderedCount += 1;
          added += 1;
        }
        // If the API returned fewer total reviews than initialTarget,
        // there's no more to load — hide Load More.
        if (this.button && reviews.length <= this.initialTarget) {
          this.button.hidden = true;
          this.allLoaded = true;
        }

        // Re-run the pictures-first reorder so the API gap-fillers
        // appended above don't sit BELOW the no-pic SSR cards. Without
        // this, the visual order ends up [SSR pics, SSR no-pics, API
        // pics] — which isn't pictures-first anymore. Only relevant
        // when the active sort is `pictures`.
        if (this.currentSort === 'pictures') this.reorderPicturesFirst();
      } catch (_) {
        // Silent failure — SSR'd cards still show. Load More will retry.
      } finally {
        clearTimeout(timeoutId);
        this.loading = false;
      }
    }

    /**
     * Apply a rating filter by refetching from Judge.me with
     * `filter_rating=N`. 0 = clear filter (show all). Paints
     * `.is-active` on the matching histogram row up front for instant
     * feedback, then routes through the same `resetAndFetch` path as a
     * sort change — `buildUrl` reads `this.activeRating` and appends
     * the param when > 0. Load More keeps working across filtered
     * pages because every page request goes through `buildUrl`.
     */
    applyRatingFilter(rating) {
      for (var j = 0; j < this.histogramRows.length; j++) {
        var rRow = parseInt(this.histogramRows[j].getAttribute('data-cd-hist-rating'), 10);
        var on = (rating !== 0 && rRow === rating);
        this.histogramRows[j].classList.toggle('is-active', on);
        this.histogramRows[j].setAttribute('aria-pressed', on ? 'true' : 'false');
      }
      this.resetAndFetch();
    }

    onLoadMore() {
      if (this.loading) return;
      this.loadMore();
    }

    onSortChange(e) {
      if (this.loading) return;
      var value = (e && e.target && e.target.value) || '';
      if (value === this.currentSort) return;
      this.currentSort = value;
      // Clear any active rating filter — sort change means "fresh
      // baseline", so any histogram pin shouldn't carry over.
      this.activeRating = 0;
      for (var i = 0; i < this.histogramRows.length; i++) {
        this.histogramRows[i].classList.remove('is-active');
        this.histogramRows[i].setAttribute('aria-pressed', 'false');
      }
      this.resetAndFetch();
    }

    /**
     * Build the API URL for the given page using the current sort.
     */
    buildUrl(page) {
      var url = 'https://judge.me/api/v1/widgets/product_review'
        + '?api_token=' + encodeURIComponent(this.token)
        + '&shop_domain=' + encodeURIComponent(this.shopDomain)
        + '&platform=shopify'
        + '&external_id=' + encodeURIComponent(this.productId)
        + '&page=' + page
        + '&per_page=' + this.perLoad;
      var params = SORT_PARAMS[this.currentSort];
      if (params) {
        if (params.sort_by) url += '&sort_by=' + encodeURIComponent(params.sort_by);
        if (params.sort_dir) url += '&sort_dir=' + encodeURIComponent(params.sort_dir);
      }
      // Histogram filter — Judge.me's widget API uses `filter_rating`.
      // Verified live; `rating`, `min_rating`, `score`, `rating[]` all
      // silently fall through to no-filter. Only the exact param name
      // works.
      if (this.activeRating >= 1 && this.activeRating <= 5) {
        url += '&filter_rating=' + this.activeRating;
      }
      return url;
    }

    /**
     * Sort changed — clear the rendered list (including SSR'd cards),
     * reset pagination state, then fetch page 1 of the new sort.
     */
    async resetAndFetch() {
      this.loading = true;
      if (this.sortSelect) this.sortSelect.disabled = true;
      if (this.button) {
        this.button.disabled = true;
        this.button.hidden = false;
        this.button.textContent = 'Loading...';
      }
      if (this.errorEl) this.errorEl.hidden = true;

      // Clear list and render skeletons for incoming page 1.
      this.list.innerHTML = '';
      this.seen = new Set();
      this.renderedCount = 0;
      this.allLoaded = false;

      var skeletonCount = Math.min(this.perLoad, 6);
      var skeletons = [];
      for (var i = 0; i < skeletonCount; i++) {
        var sk = buildSkeleton();
        this.list.appendChild(sk);
        skeletons.push(sk);
      }

      // 10s hard timeout — prevents an indefinite hang if Judge.me stalls.
      var controller = (typeof AbortController !== 'undefined') ? new AbortController() : null;
      var timeoutId = setTimeout(function () { if (controller) controller.abort(); }, 10000);

      try {
        var fetchOpts = { method: 'GET', headers: { Accept: 'application/json' } };
        if (controller) fetchOpts.signal = controller.signal;
        var res = await fetch(this.buildUrl(1), fetchOpts);
        clearTimeout(timeoutId);
        if (!res.ok) throw new Error('HTTP ' + res.status);
        var json = await res.json();
        var reviews = parseReviews(json && json.widget);

        for (var s = 0; s < skeletons.length; s++) {
          if (skeletons[s].parentNode) skeletons[s].remove();
        }

        if (reviews.length === 0) {
          if (this.errorEl) {
            this.errorEl.hidden = false;
            this.errorEl.textContent = (this.activeRating >= 1 && this.activeRating <= 5)
              ? 'No ' + this.activeRating + '-star reviews to show.'
              : 'No reviews to show for this filter.';
          }
          if (this.button) this.button.hidden = true;
          return;
        }

        for (var j = 0; j < reviews.length; j++) {
          if (this.renderedCount >= this.maxTotal) break;
          var node = buildCard(reviews[j], j, this.renderedCount);
          this.list.appendChild(node);
          this.seen.add(reviews[j].id);
          this.renderedCount += 1;
        }

        // After a fresh page-1 fetch, the next paginate target is page 2.
        this.nextPage = 2;

        if (this.button) {
          if (reviews.length < this.perLoad || this.renderedCount >= this.maxTotal) {
            this.button.hidden = true;
            this.allLoaded = true;
          } else {
            this.button.hidden = false;
            this.button.disabled = false;
            this.button.textContent = this.loadMoreLabel;
          }
        }
      } catch (err) {
        clearTimeout(timeoutId);
        for (var k = 0; k < skeletons.length; k++) {
          if (skeletons[k].parentNode) skeletons[k].remove();
        }
        if (this.errorEl) {
          this.errorEl.hidden = false;
          this.errorEl.textContent = "Couldn't load reviews. Try again in a moment.";
        }
        if (this.button) {
          this.button.disabled = false;
          this.button.textContent = this.loadMoreLabel;
        }
        if (window.console && console.warn) console.warn('[cd-product-reviews] sort fetch failed:', err);
      } finally {
        this.loading = false;
        if (this.sortSelect) this.sortSelect.disabled = false;
      }
    }

    async loadMore() {
      this.loading = true;
      this.button.disabled = true;
      if (this.sortSelect) this.sortSelect.disabled = true;
      var origLabel = this.origLabel || this.button.textContent;
      this.origLabel = origLabel;
      this.button.textContent = 'Loading...';
      if (this.errorEl) this.errorEl.hidden = true;

      // Skeleton placeholder.
      var skeleton = buildSkeleton();
      this.list.appendChild(skeleton);

      // 10s hard timeout — prevents the button from hanging on
      // "Loading..." if Judge.me's API stalls.
      var controller = (typeof AbortController !== 'undefined') ? new AbortController() : null;
      var timeoutId = setTimeout(function () { if (controller) controller.abort(); }, 10000);

      try {
        var fetchOpts = { method: 'GET', headers: { Accept: 'application/json' } };
        if (controller) fetchOpts.signal = controller.signal;
        var res = await fetch(this.buildUrl(this.nextPage), fetchOpts);
        clearTimeout(timeoutId);
        if (!res.ok) throw new Error('HTTP ' + res.status);
        var json = await res.json();
        var reviews = parseReviews(json && json.widget);

        skeleton.remove();

        // De-dupe against already-rendered IDs.
        var fresh = reviews.filter(function (r) { return !this.seen.has(r.id); }, this);

        var addedCount = 0;
        for (var i = 0; i < fresh.length; i++) {
          if (this.renderedCount >= this.maxTotal) break;
          var node = buildCard(fresh[i], i, this.renderedCount);
          this.list.appendChild(node);
          this.seen.add(fresh[i].id);
          this.renderedCount += 1;
          addedCount += 1;
        }

        this.nextPage += 1;

        // Restore label first, THEN decide whether to hide. Avoids
        // a stuck "Loading..." label sticking around if the button
        // is later revealed by other state changes.
        this.button.textContent = origLabel;
        this.button.disabled = false;

        // Hide the button if we've truly hit the end:
        //   - 0 fresh reviews returned (all duplicates)
        //   - 0 actually appended (cap hit before any render)
        //   - API returned a partial page (fewer than per_page)
        //   - rendered count reached the user-configured cap
        if (addedCount === 0
            || reviews.length < this.perLoad
            || this.renderedCount >= this.maxTotal) {
          this.button.hidden = true;
          this.allLoaded = true;
        }
      } catch (err) {
        clearTimeout(timeoutId);
        if (skeleton.parentNode) skeleton.remove();
        if (this.errorEl) {
          this.errorEl.hidden = false;
          this.errorEl.textContent = this.defaultErrorText;
        }
        this.button.disabled = false;
        this.button.textContent = origLabel;
        if (window.console && console.warn) console.warn('[cd-product-reviews] load more failed:', err);
      } finally {
        this.loading = false;
        if (this.sortSelect) this.sortSelect.disabled = false;
      }
    }
  }

  customElements.define('cd-product-reviews-fetcher', CDProductReviewsFetcher);
})();
